import clsx from 'clsx';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { lerNumero, paraCampo } from '../lib/formatos';

interface Props {
  valor: number | null;
  /** Chamado ao sair do campo ou com Enter, só quando o valor mudou. Esc desfaz. */
  aoConfirmar: (valor: number | null) => unknown;
  rotulo: string;
  casas?: number;
  /** Mostra sempre todas as casas (preços: "5,10"). */
  casasFixas?: boolean;
  sufixo?: string;
  permitirVazio?: boolean;
  desabilitado?: boolean;
  destaque?: boolean;
  className?: string;
  placeholder?: string;
}

/** Campo numérico para edição inline em tabelas, no formato brasileiro (vírgula decimal). */
export function EntradaNumero({ valor, aoConfirmar, rotulo, casas = 6, casasFixas = false, sufixo, permitirVazio = false, desabilitado, destaque, className, placeholder }: Props) {
  const [texto, setTexto] = useState(paraCampo(valor, casas, casasFixas));
  const [invalido, setInvalido] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const focado = useRef(false);
  const cancelado = useRef(false);

  // Valor novo vindo do servidor (recálculo) substitui o texto, exceto durante a digitação.
  useEffect(() => {
    if (!focado.current) {
      setTexto(paraCampo(valor, casas, casasFixas));
      setInvalido(false);
    }
  }, [valor, casas, casasFixas]);

  async function confirmar() {
    const n = lerNumero(texto);
    if (n === null && !permitirVazio) {
      setTexto(paraCampo(valor, casas, casasFixas));
      return;
    }
    if (n !== null && (Number.isNaN(n) || n < 0)) {
      setInvalido(true);
      return;
    }
    setInvalido(false);
    const mesmo = n === null ? valor === null : valor !== null && Math.abs(n - valor) < 10 ** -(casas + 1);
    if (mesmo) {
      setTexto(paraCampo(valor, casas, casasFixas));
      return;
    }
    setSalvando(true);
    try {
      await aoConfirmar(n);
    } catch {
      setTexto(paraCampo(valor, casas, casasFixas));
    } finally {
      setSalvando(false);
    }
  }

  function tecla(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      setTexto(paraCampo(valor, casas, casasFixas));
      setInvalido(false);
      cancelado.current = true;
      e.currentTarget.blur();
    }
  }

  return (
    <div className={clsx('relative inline-flex w-full items-center', className)}>
      <input
        type="text"
        inputMode="decimal"
        aria-label={rotulo}
        aria-invalid={invalido || undefined}
        title={invalido ? 'Número inválido' : undefined}
        disabled={desabilitado || salvando}
        value={texto}
        placeholder={placeholder}
        onFocus={(e) => {
          focado.current = true;
          e.currentTarget.select();
        }}
        onBlur={() => {
          focado.current = false;
          if (cancelado.current) {
            cancelado.current = false;
            return;
          }
          void confirmar();
        }}
        onChange={(e) => setTexto(e.target.value)}
        onKeyDown={tecla}
        className={clsx(
          'numero h-7 w-full rounded-campo border px-1.5 text-right text-[13px] transition-colors duration-150 hover:border-tinta-2/50 disabled:bg-fundo disabled:text-tinta-2',
          sufixo && 'pr-5',
          invalido ? 'border-erro bg-erro-fundo' : destaque ? 'border-editado/50 bg-editado-fundo font-medium text-editado' : 'border-linha bg-superficie',
        )}
      />
      {sufixo && <span className="pointer-events-none absolute right-1.5 text-[12px] text-tinta-2">{sufixo}</span>}
    </div>
  );
}
